import { Key } from "ts-key-enum";
import {
  InjectionKey,
  Ref,
  computed,
  inject,
  provide,
  reactive,
  ref,
  shallowReadonly,
  watch,
  onUnmounted,
} from "vue";
import { isFunction, promisedTimeout, useEvent } from "vue-composable";

let loadingCount = 0;
export function startLoading() {
  return ++loadingCount;
}

export function stopLoading() {
  return --loadingCount;
}

async function waitLoading() {
  while (loadingCount > 0) {
    await new Promise((resolve) => setTimeout(resolve, 10));
  }
}

function getCSSPath(el: Element | null): string | undefined {
  if (!el || !(el instanceof Element)) return undefined;

  const path: string[] = [];
  while (el && el.nodeType === Node.ELEMENT_NODE) {
    let selector = el.nodeName.toLowerCase();
    if (el.id) {
      selector += `#${el.id}`;
      path.unshift(selector);
      break;
    } else {
      let sibling: Element | null = el;
      let nth = 1;
      while ((sibling = sibling.previousElementSibling)) {
        if (sibling.nodeName.toLowerCase() === selector) nth++;
      }
      if (nth !== 1) selector += `:nth-of-type(${nth})`;
    }
    path.unshift(selector);
    el = el.parentNode as Element | null;
  }

  return path.join(" > ");
}

const FocusHistorySymbol = Symbol(
  IS_APP && import.meta.env.DEV ? "FocusHistory" : undefined,
) as InjectionKey<{
  queue: readonly HTMLElement[];
  focusLast: () => void;
}>;

export function defineFocusHistory(queueMax: number | undefined = 500) {
  const queue = reactive<HTMLElement[]>([]);
  const cssMap = new WeakMap<HTMLElement, string>();
  useEvent(
    window,
    "focus",
    (e) => {
      const target = e.relatedTarget ?? e.target;

      if (
        target instanceof HTMLElement && // ensure it's a real element
        document.body.contains(target) && // safe now
        isAllowedToFocus(target)
      ) {
        queue.push(target as HTMLElement);
        cssMap.set(target as HTMLElement, getCSSPath(target as HTMLElement));
      }
    },
    {
      capture: true,
      passive: true,
    },
  );

  async function focusLast() {
    let item;

    await waitLoading();
    loadingCount = 0;

    await promisedTimeout(10);
    while ((item = queue.pop())) {
      if (!document.body.contains(item)) {
        const p = cssMap.get(item);
        if (p) {
          try {
            const e = document.querySelector(p);
            if (e && e instanceof HTMLElement && e.focus) {
              e.focus();
              return;
            }
          } catch {
            //ignore
          }
        }
        continue;
      }
      if (item && item instanceof HTMLElement && item.focus) {
        item.focus();
        return;
      }
    }
  }

  if (queueMax) {
    watch(
      () => queue.length,
      (e) => {
        if (queueMax >= e) return;
        // while (e > queueMax) {
        //   queue.shift();
        // }
        while (queue.length > queueMax) {
          queue.shift();
        }
      },
      {
        flush: "post",
      },
    );
  }

  const result = {
    queue: shallowReadonly(queue),

    focusLast,
  };

  provide(FocusHistorySymbol, result);

  return result;
}

export function useFocusHistory() {
  return inject(FocusHistorySymbol);
}

const focusableSelector = [
  'button:not([disabled]):not([disable]):not([tabindex="-1"])',
  'a[href]:not([tabindex="-1"])',
  'input:not([disabled]):not([disable]):not([tabindex="-1"])',
  'select:not([disabled]):not([disable]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([disable]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"]):not([disabled]):not([disable])',
].join(", ");

function isAllowedToFocus(x: HTMLElement) {
  if (!isFunction(x.getAttribute)) return undefined;
  return (
    x.getAttribute("tabindex") !== "-1" && !x.hasAttribute("disabled") && !x.hasAttribute("disable")
  );
}

export function useFocusTrap(
  el: Ref<HTMLElement | undefined | null>,
  autoFocus: boolean | number,
  returnFocus: boolean = false,
) {
  const focusQueue = useFocusHistory();

  const focusableContent = ref<HTMLElement[] | null | undefined>(null);
  const runCount = ref(0);
  const startFocusOn = ref<HTMLElement | null>(null);

  const startedWithFocusOn: HTMLElement | null = focusQueue?.queue[0]
    ? focusQueue?.queue[0]
    : document.activeElement;

  const observer = new MutationObserver(refreshElements);

  function isDomNode(x: unknown): x is Node {
    return typeof Node !== "undefined" && x instanceof Node;
  }

  let focusTarget: EventTarget | null = null;

  watch(el, (e) => {
    setupObserver(e);
  });

  function setupObserver(e: HTMLElement | null | undefined) {
    try {
      observer?.disconnect();
    } catch {
      // ignore
    }

    const target: Node | null = isDomNode(e) ? e : isDomNode(el?.value) ? el!.value : null;

    if (target) {
      Promise.resolve().then(refreshElements);
      try {
        observer.observe(target, {
          childList: true,
          subtree: true,
          attributeFilter: ["tabindex", "disable", "disabled"],
          attributes: true,
        });
      } catch (err) {
        // As a final safeguard, avoid throwing in production
        console.warn("MutationObserver.observe failed:", err);
      }
    } else {
      focusTarget = null;
    }
  }

  function refreshElements() {
    // Collect allowed focusables directly via the selector (no extra filter needed)
    focusableContent.value = Array.from(
      el?.value?.querySelectorAll<HTMLElement>(focusableSelector) ?? [],
    );

    // TODO: get unfiltered ref html, check for a form
    //  and focus first reasonable input
    handleStartFocus();
  }

  function handleStartFocus() {
    if (focusableContent.value && focusableContent.value.length > 0 && runCount.value === 0) {
      startFocusOn.value =
        focusableContent.value.find((el: HTMLElement) => el.hasAttribute("data-focus-first")) ??
        null;
      startFocusOn.value?.focus();
      runCount.value++;
      return;
    }
  }

  // can be simplified when upgraded to Vue > 3.5
  // could be built out to be more selective, ie first instance of x.
  const index =
    autoFocus === true ? 0 : typeof autoFocus === "number" ? Math.max(0, autoFocus) : null;

  if (index !== null) {
    const stop: WatchStopHandle | undefined = watch(
      focusableContent,
      (els) => {
        const el = startFocusOn.value ? startFocusOn.value : els?.[index];
        if (el?.focus) {
          el.focus();
          // defer so `stop` is initialized before we call it
          queueMicrotask(() => stop?.());
        }
      },
      { flush: "post", immediate: true },
    );
  }

  const resetOnCurrentRef = (modalRef) => {
    runCount.value = 0;
    setupObserver(isDomNode(modalRef) ? (modalRef as HTMLElement) : null);
  };

  const first = computed(() => focusableContent.value?.[0]);
  const last = computed(() =>
    focusableContent.value ? focusableContent.value[focusableContent.value.length - 1] : undefined,
  );

  useEvent(
    el,
    "focus",
    (e) => {
      focusTarget = e.target;
    },
    {
      capture: true,
    },
  );

  useEvent(document, "keydown", (e) => {
    if (!el.value) return;
    const isTabPressed = e.key === Key.Tab || e.keyCode === 9;

    if (!isTabPressed) {
      return;
    }

    if (e.shiftKey || e.metaKey) {
      if (focusTarget === first.value) {
        last.value?.focus();
        e.preventDefault();
      }
    } else {
      if (focusTarget === last.value) {
        first.value?.focus();
        e.preventDefault();
      }
    }
  });

  const returnFocusToParent = () => {
    if (returnFocus) {
      startedWithFocusOn?.focus();
    }
  };

  onUnmounted(() => returnFocusToParent());

  return {
    resetOnCurrentRef,
    focusableContent,
    first,
    last,
  };
}
